import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META } from "@/data/mapConfig";
import { Plus, Pencil, Trash2, ArrowLeft, LogOut, X, Loader2, Download } from "lucide-react";
import { downloadProvidersCsv } from "@/lib/exportCsv";

const EMPTY = {
  name: "", company: "", category: "diagnostic_imaging", address: "", city: "", state: "TX",
  zip: "", phone: "", lat: "", lng: "", number: 0, pinned: true, note: "",
};

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/locations").then((res) => setLocations(res.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (loc) => { setEditing(loc); setForm({ ...loc }); setShowForm(true); };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"?`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success("Location deleted");
      setLocations((prev) => prev.filter((l) => l.id !== loc.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      lat: parseFloat(form.lat) || 0,
      lng: parseFloat(form.lng) || 0,
      number: parseInt(form.number) || 0,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/locations/${editing.id}`, payload);
        setLocations((prev) => prev.map((l) => (l.id === editing.id ? data : l)));
        toast.success("Location updated");
      } else {
        const { data } = await api.post("/locations", payload);
        setLocations((prev) => [...prev, data]);
        toast.success("Location added");
      }
      setShowForm(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const shown = filter === "all" ? locations : locations.filter((l) => l.category === filter);

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <header className="flex flex-wrap items-center justify-between gap-2 px-5 sm:px-8 py-3 min-h-[68px] bg-[#0B132B] border-b border-white/10 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-back-link" className="text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-lg sm:text-xl font-bold">Location Management</h1>
          <span className="text-xs text-slate-500 font-mono">{locations.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-export-csv-btn"
            onClick={() => toast.success(`Exported ${downloadProvidersCsv(locations)} providers`)}
            className="flex items-center gap-2 text-slate-200 hover:text-white text-sm font-semibold px-3.5 py-2 rounded-lg border border-white/15 hover:border-white/40 transition-all"
          >
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Export Spreadsheet</span>
          </button>
          <button
            data-testid="admin-add-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-3.5 py-2 rounded-lg transition-all"
          >
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Location</span>
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-2 text-slate-300 hover:text-white text-sm px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 transition-all"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-5 sm:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {["all", ...Object.keys(CATEGORY_META)].map((c) => (
            <button
              key={c}
              data-testid={`admin-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                filter === c ? "bg-white text-[#0B132B]" : "bg-[#1A275B] text-slate-300 hover:bg-[#22305f]"
              }`}
            >
              {c === "all" ? "All" : CATEGORY_META[c].label}
            </button>
          ))}
        </div>

        <div className="bg-[#111D4A] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10 text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 font-semibold">#</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold">City</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                ) : shown.map((loc) => {
                  const meta = CATEGORY_META[loc.category];
                  return (
                    <tr key={loc.id} data-testid={`admin-row-${loc.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 font-mono text-slate-400">{loc.number}</td>
                      <td className="px-4 py-3 font-semibold text-white">
                        {loc.company ? (
                          <>
                            <span className="block">{loc.company}</span>
                            <span className="block text-xs font-normal text-blue-300">{loc.name}</span>
                          </>
                        ) : (
                          loc.name
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full" style={{ background: `${meta?.color}22`, color: meta?.color }}>
                          <span className="w-2 h-2 rounded-full" style={{ background: meta?.color }} />
                          {meta?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{loc.address}</td>
                      <td className="px-4 py-3 text-slate-300">{loc.city}, {loc.state}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`admin-edit-${loc.id}`} onClick={() => openEdit(loc)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button data-testid={`admin-delete-${loc.id}`} onClick={() => handleDelete(loc)} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
          <form
            data-testid="admin-form"
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="bg-[#111D4A] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto dfw-scroll"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#111D4A]">
              <h2 className="font-heading text-lg font-bold">{editing ? "Edit Location" : "Add Location"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Location / Area Name" full><input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. Flower Mound" /></Field>
              <Field label="Company / Practice Name" full><input data-testid="form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} placeholder="e.g. Injury & Pain Physicians" /></Field>
              <Field label="Category">
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  {Object.entries(CATEGORY_META).map(([k, v]) => (
                    <option key={k} value={k} className="bg-[#1A275B]">{v.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Pin Number"><input data-testid="form-number" type="number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputCls} /></Field>
              <Field label="Address" full><input data-testid="form-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
              <Field label="City"><input data-testid="form-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></Field>
              <Field label="State"><input data-testid="form-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} /></Field>
              <Field label="ZIP"><input data-testid="form-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={inputCls} /></Field>
              <Field label="Latitude"><input data-testid="form-lat" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className={inputCls} /></Field>
              <Field label="Longitude"><input data-testid="form-lng" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className={inputCls} /></Field>
              <Field label="Note / Badge (e.g. Virtual / Statewide)" full><input data-testid="form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} placeholder="Optional badge text" /></Field>
              <div className="col-span-2">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input data-testid="form-pinned" type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="w-4 h-4 accent-blue-500" />
                  <span className="text-sm text-slate-200">Show as map pin <span className="text-slate-400">(uncheck for info-only / virtual services with no address)</span></span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 bg-[#111D4A]">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 text-sm font-medium transition-all">Cancel</button>
              <button data-testid="form-save" type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#1A275B] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-400 focus:outline-none";

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs font-semibold text-slate-300 uppercase tracking-wide">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
